/**
 * scheduleDraftPayloadBuilders — ScheduleDraft 写操作 payload 的语义组装层。
 *
 * 本模块把创建草稿、航班写入和来源航班导入的表单值转换为后端契约字段。
 * 它隔离 UI 表单形状与 API payload 形状，让 mutation hooks 只处理语义对象
 * 和缓存生命周期。
 *
 * 设计文档 → docs/10_core_contracts/10_data_model.md
 * 骨架关联 → hooks/useScheduleDraftFlightMutations.js (航班 mutation), hooks/useScheduleDraftEditorMutations.js (来源导入), components/ScheduleDraftFlightForm.jsx (表单输入)
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { normalizeStcTypeEmptyValue } from "../constants/scheduleDraftOptions";
import { DEFAULT_TRANSPORT_DEMANDS } from "../constants/transportDemandDefaults";
import { getIsoDay, normalizeTimeValue } from "../lib/flightTimingUtils";

dayjs.extend(utc);

export { DEFAULT_TRANSPORT_DEMANDS };

/**
 * 将创建草稿表单值转换为 ScheduleDraft 创建 payload。
 * @param {Object} values - 创建弹窗表单值。
 * @returns {Object} 后端创建草稿 payload。
 */
export function buildCreateScheduleDraftPayload(values = {}) {
    const sourceType = values.sourceType || "BLANK";
    const payload = {
        source_type: sourceType,
        description: values.description?.trim() || undefined,
    };

    if (values.name?.trim()) {
        payload.name = values.name.trim();
    }

    if (values.periodRange?.[0] && values.periodRange?.[1]) {
        payload.period_start = values.periodRange[0].format("YYYY-MM-DD");
        payload.period_end = values.periodRange[1].format("YYYY-MM-DD");
    }

    if (values.aircraftScope?.length) {
        payload.aircraft_scope = values.aircraftScope;
    }

    if (sourceType === "FROM_V1" && values.sourceScheduleId) {
        payload.source_v1_schedule_ids = [Number(values.sourceScheduleId)];
    }

    // 空白草稿仍需要需求锚点，先注入默认模板支撑工作台观察骨架
    if (sourceType === "BLANK") {
        payload.transport_demands = DEFAULT_TRANSPORT_DEMANDS;
    }

    return payload;
}

export function buildCreateScheduleDraftSubmission(values = {}) {
    const createPayload = buildCreateScheduleDraftPayload(values);
    return { createPayload, pendingSourceImport: null };
}

/**
 * 将航班表单或表格编辑值转换为 ScheduleDraftFlight 写入 payload。
 * @param {Object} values - 航班编辑值。
 * @returns {Object} 后端航班写入 payload。
 */
export function buildScheduleDraftFlightPayload(values = {}) {
    const stdValue = normalizeTimeValue(values.std);
    const staValue = normalizeTimeValue(values.sta);
    const std = stdValue ? dayjs.utc(stdValue) : null;
    const sta = staValue ? dayjs.utc(staValue) : null;
    const hasValidSchedule = Boolean(std?.isValid() && sta?.isValid());
    const hasBlockTime = Object.prototype.hasOwnProperty.call(values, "block_time_minutes") && values.block_time_minutes !== undefined;
    const blockTimeMinutes = hasBlockTime ? (values.block_time_minutes == null ? null : Number(values.block_time_minutes)) : hasValidSchedule ? Math.max(sta.diff(std, "minute"), 0) : undefined;
    const minBlockTimeMinutes = Object.prototype.hasOwnProperty.call(values, "min_block_time_minutes") && values.min_block_time_minutes !== undefined
        ? (values.min_block_time_minutes == null ? null : Number(values.min_block_time_minutes))
        : undefined;
    const displayOrder = values.display_order == null ? undefined : Number(values.display_order);
    const hasStcType = Object.prototype.hasOwnProperty.call(values, "stc_type");
    const stcType = hasStcType ? normalizeStcTypeEmptyValue(values.stc_type) : undefined;

    return {
        aircraft_reg: values.aircraft_reg,
        flight_number: values.flight_number,
        departure_airport: values.departure_airport,
        arrival_airport: values.arrival_airport,
        flight_route_id: values.flight_route_id || null,
        std: stdValue,
        sta: staValue,
        flight_date: std?.isValid() ? std.format("YYYY-MM-DD") : undefined,
        day_of_week: std?.isValid() ? getIsoDay(std) : undefined,
        stc_type: hasStcType ? stcType : undefined,
        block_time_minutes: blockTimeMinutes,
        min_block_time_minutes: minBlockTimeMinutes,
        display_order: Number.isFinite(displayOrder) ? displayOrder : undefined,
        is_fixed: Boolean(values.is_fixed),
        remarks: values.remarks?.trim() || undefined,
        change_source: "USER",
    };
}

/**
 * 将来源轨筛选表单值转换为预览/导入来源航班 payload。
 * @param {Object} values - 来源轨筛选值。
 * @returns {Object} 后端来源航班 payload。
 */
export function buildScheduleDraftSourceFlightsPayload(values = {}) {
    const payload = {
        source_type: values.sourceType,
    };

    if (values.target_track_id) {
        payload.target_track_id = values.target_track_id;
    }

    const overrideAircraftReg = values.override_aircraft_reg || values.overrideAircraftReg;
    if (overrideAircraftReg) {
        payload.override_aircraft_reg = overrideAircraftReg;
    }

    const sourceId = values.source_id || values.sourceId || values.sourceScheduleId || values.sourceTrackId;
    if (sourceId) {
        payload.source_id = String(sourceId);
    }

    if (values.paired_with_track_id) {
        payload.paired_with_track_id = values.paired_with_track_id;
    }

    if (values.dateShiftTarget) {
        const dateShiftTarget = dayjs(values.dateShiftTarget);
        if (dateShiftTarget.isValid()) {
            payload.date_shift_target = dateShiftTarget.format("YYYY-MM-DD");
        }
    }

    if (values.sourceType === "EXECUTION") {
        const sourceAircraftReg = values.source_aircraft_reg || values.sourceAircraftReg;
        if (sourceAircraftReg) {
            payload.source_aircraft_reg = sourceAircraftReg;
        }
        if (values.periodRange?.[0] && values.periodRange?.[1]) {
            payload.start_date = values.periodRange[0].format("YYYY-MM-DD");
            payload.end_date = values.periodRange[1].format("YYYY-MM-DD");
        }
    }

    return payload;
}

/**
 * 将 Excel/粘贴表格中间模型转换为 ScheduleDraft 表格导入 payload。
 * @param {Object} values - 导入弹窗表单值。
 * @param {Object} tableModel - TableImportModel。
 * @returns {Object} 后端表格预览/导入 payload。
 */
export function buildScheduleDraftTableImportPayload(values = {}, tableModel = {}) {
    const payload = {
        input_kind: tableModel.input_kind,
        source_name: tableModel.source_name,
        headers: tableModel.headers || [],
        rows: tableModel.rows || [],
    };

    if (values.target_track_id) {
        payload.target_track_id = values.target_track_id;
    }

    const overrideAircraftReg = values.override_aircraft_reg || values.overrideAircraftReg;
    if (overrideAircraftReg) {
        payload.override_aircraft_reg = overrideAircraftReg;
    }

    return payload;
}
